/**
 * Explorations somebody has pinned, so they stand at the head of the sidebar's
 * list whatever has been saved since.
 *
 * A PIN IS A FACT ABOUT THE LIST, NOT ABOUT THE DOCUMENT. The project label
 * rides on the session and goes wherever the document goes — into an export,
 * into a fork, onto another device through a share — because which project an
 * exploration belongs to is something the exploration knows about itself. Which
 * rows this device keeps at hand is not: an exploration shared to a colleague
 * must not arrive pinned to the top of their pane because it was pinned to the
 * top of yours, and a fork of a pinned exploration is a new thing that has not
 * earned a pin yet. So the pins are a device-local list of ids in the codec's
 * own table, and nothing in a document says whether it is pinned — which is
 * also why there is no schema bump and no migration here.
 *
 * NEWEST PIN FIRST, and that is the one order this pane keeps that is not
 * recency of work. `ui/fileDrag.ts` refuses a hand-sorted list because a row's
 * position is a fact about when it was last worked on, and a pin is the one
 * case where that is exactly the wrong fact: the point of pinning something is
 * that it stays where you left it while you work on other things. The order is
 * still a fact and not a ranking — when each pin was made — so a pinned row
 * never moves until another is pinned above it or it is taken down.
 *
 * A STALE ID DRAWS NOTHING, AND NO READ PRUNES IT. The list is joined against
 * the index rows on every read, so a pin whose exploration is gone is simply
 * absent from the section. It is deliberately not reconciled against the rows a
 * page can see, the way the declared projects are: a page without a shelf sees
 * only a working copy of the library (`openableRows`), and a prune against that
 * view would take the pins of every exploration a later boot can read again.
 * The one door that takes a pin down is the one that takes the exploration
 * down — `forgetSession`, which is why the slot lives in `persist.ts`'s table
 * rather than the store's.
 *
 * Pure, and it imports nothing. `persist.ts` owns the slot and is the only
 * caller that touches storage.
 */

/**
 * How many explorations one device may pin.
 *
 * A bound on a runaway, not a budget somebody is meant to feel: forty is past
 * the point where a pinned section is a shortcut rather than a second copy of
 * the list. Past it the OLDEST pin falls off rather than the new one being
 * refused, for the reason `core/projects.ts` gives for its own cap — a refusal
 * is a sentence about a limit nobody has been told about, at the exact moment
 * somebody is reaching for something.
 */
export const MAX_PINNED = 40

/**
 * The pinned ids as they come off disk — validated, deduped and capped.
 *
 * Anything that is not an array of non-empty strings reads as no pins: this is
 * bytes off a shared origin like every other slot, and a hand-edited row must
 * not draw a section no writer in this app could have produced.
 */
export function parsePinned(raw: string | null): string[] {
  if (!raw) return []
  let list: unknown
  try {
    list = JSON.parse(raw)
  } catch {
    return []
  }
  if (!Array.isArray(list)) return []
  const out: string[] = []
  for (const item of list) {
    if (typeof item !== 'string' || !item || out.includes(item)) continue
    out.push(item)
    if (out.length === MAX_PINNED) break
  }
  return out
}

export function serialisePinned(ids: readonly string[]): string {
  return JSON.stringify(ids)
}

/**
 * Pin one exploration, at the front. A pin is a STATE rather than an event, so
 * pinning one that is already pinned changes nothing — not even its place —
 * and an empty id pins nothing.
 */
export function withPinned(ids: readonly string[], id: string): string[] {
  if (!id || ids.includes(id)) return [...ids]
  return [id, ...ids].slice(0, MAX_PINNED)
}

/** Take one exploration off the list. Unchanged when it was not on it. */
export function withoutPinned(ids: readonly string[], id: string): string[] {
  return ids.filter((p) => p !== id)
}

/**
 * The pinned rows, in pin order, out of whatever rows the caller has.
 *
 * The sidebar hands it every row it has — nothing in the pane filters since
 * D102 moved the search to a sheet — so a pinned row is drawn again exactly
 * when its row is. An id with no row draws nothing — see the header for why it
 * is left standing rather than pruned.
 */
export function pinnedRows<T extends { id: string }>(
  ids: readonly string[],
  rows: readonly T[],
): T[] {
  const byId = new Map(rows.map((r) => [r.id, r]))
  const out: T[] = []
  for (const id of ids) {
    const row = byId.get(id)
    if (row) out.push(row)
  }
  return out
}
